// expenseCategorySlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import Source from '../../Source';

// Async thunks for API calls
export const getExpenseCategories = createAsyncThunk('expenseCategories/getExpenseCategories', async () => {
  const response = await axios.get(`${Source.getAddress()}/api/expense/category`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("access")}`,
    },
  });
  return response.data;
});

export const createExpenseCategory = createAsyncThunk('expenseCategories/createExpenseCategory', async (categoryData) => {
  const response = await axios.post(`${Source.getAddress()}/api/expense/category`, categoryData, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("access")}`,
    },
  });
  return response.data;
});

export const updateExpenseCategory = createAsyncThunk('expenseCategories/updateExpenseCategory', async ({ id, categoryData }) => {
  const response = await axios.put(`${Source.getAddress()}/api/expense/category/${id}`, categoryData, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("access")}`,
    },
  });
  return response.data;
});

export const deleteExpenseCategory = createAsyncThunk('expenseCategories/deleteExpenseCategory', async (id) => {
  await axios.delete(`${Source.getAddress()}/api/expense/category/${id}`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("access")}`,
    },
  });
  return id;
});

export const getExpenseCategoryById = createAsyncThunk('expenseCategories/getExpenseCategoryById', async (id) => {
  const response = await axios.get(`${Source.getAddress()}/api/expense/category/${id}`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("access")}`,
    },
  });
  return response.data;
});

// Expense Category Slice
const expenseCategorySlice = createSlice({
  name: 'expenseCategories',
  initialState: {
    categories: [], // List of expense categories
    currentCategory: null, // Currently selected category
    loading: false, // Loading state
    error: null, // Error state
    status: null, // Status for async operations
  },
  reducers: {
    // Add category to cache
    addCategoryToCache: (state, action) => {
      state.categories.push(action.payload);
    },
    // Update category in cache
    updateCategoryInCache: (state, action) => {
      const { id, updatedData } = action.payload;
      const index = state.categories.findIndex((category) => category.id === id);
      if (index !== -1) {
        state.categories[index] = { ...state.categories[index], ...updatedData };
      }
    },
    // Delete category from cache
    deleteCategoryFromCache: (state, action) => {
      const id = action.payload;
      state.categories = state.categories.filter((category) => category.id !== id);
    },
    // Get category from cache
    getCategoryFromCache: (state, action) => {
      const id = action.payload;
      state.currentCategory = state.categories.find((category) => category.id === id);
    },
    // Clear current category
    clearCurrentCategory: (state) => {
      state.currentCategory = null;
    },
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all categories
      .addCase(getExpenseCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(getExpenseCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
        state.status = 'succeeded';
      })
      .addCase(getExpenseCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.status = 'rejected';
      })
      // Create category
      .addCase(createExpenseCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(createExpenseCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.categories.push(action.payload);
        state.status = 'created';
      })
      .addCase(createExpenseCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.status = 'rejected';
      })
      // Update category
      .addCase(updateExpenseCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(updateExpenseCategory.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.categories.findIndex((category) => category.id === action.payload.id);
        if (index !== -1) {
          state.categories[index] = action.payload;
        }
        state.status = 'updated';
      })
      .addCase(updateExpenseCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.status = 'rejected';
      })
      // Delete category
      .addCase(deleteExpenseCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(deleteExpenseCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = state.categories.filter((category) => category.id !== action.payload);
        state.status = 'deleted';
      })
      .addCase(deleteExpenseCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.status = 'rejected';
      })
      // Get category by ID
      .addCase(getExpenseCategoryById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(getExpenseCategoryById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCategory = action.payload;
        state.status = 'succeeded';
      })
      .addCase(getExpenseCategoryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.status = 'rejected';
      });
  },
});

// Export actions and reducer
export const {
  addCategoryToCache,
  updateCategoryInCache,
  deleteCategoryFromCache,
  getCategoryFromCache,
  clearCurrentCategory,
  clearError,
} = expenseCategorySlice.actions;

export default expenseCategorySlice.reducer;